/**
 * Socket.IO 이벤트 → State 전이 Orchestration — Plan 05-05 Task 2.
 *
 * WidgetSocketHandlers 를 createHandlers(state, tts, theme, options) 팩토리로 구성.
 * 각 WS 이벤트를 수신해 state.transitionTo / tts.play / pauseQueue/resumeQueue 로 변환.
 *
 * 핵심 규칙 (DESIGN.md §3.1):
 *   - donation.completed.v1 → tier 결정 → donation-incoming-<tier>
 *   - donation.tts_ready.v1 → tts-playing → await tts.play → idle
 *   - donation.tts_failed.v1 → transition 없음 (toast 자연 종료)
 *   - donation.filter_triggered.v1 → filter-triggered (viewer 뷰는 render 측에서 숨김)
 *   - goal.reached.v1 → pauseQueue → goal-reached 10s → resumeQueue + idle
 *
 * 보안 (T-05-27 Spoofing audioUrl):
 *   - onTtsReady 에서 audioUrl prefix === apiBase 검증 — 다른 origin 의 audio 재생 거부.
 *   - apiBase 미지정이면 검증 skip (테스트 격리 편의).
 */
import type { Theme } from '@loop/shared/theme';
import type {
  WidgetDonationToast,
  WidgetTtsReady,
  WidgetTtsFailed,
  WidgetFilterTriggered,
  WidgetGoalReached,
} from '@loop/contracts/ws';
import type { StateController } from '../state/orchestrator.js';
import type { TtsPlayer } from '../audio/player.js';
import type { WidgetSocketHandlers } from '../socket/client.js';

export interface CreateHandlersOptions {
  readonly state: StateController;
  readonly tts: TtsPlayer;
  readonly theme: Theme;
  /** T-05-27 audioUrl prefix 검증용. 미지정 시 검증 skip. */
  readonly apiBase?: string;
  /** goal-reached 연출 지속 시간 (ms). 기본 10_000 (DESIGN §3.1 #11). */
  readonly goalDurationMs?: number;
}

const DEFAULT_GOAL_DURATION_MS = 10_000;

export function createHandlers(opts: CreateHandlersOptions): WidgetSocketHandlers {
  const goalDur = opts.goalDurationMs ?? DEFAULT_GOAL_DURATION_MS;

  function onDonation(p: WidgetDonationToast): void {
    // 서버측 tier 를 그대로 신뢰 (Plan 05-02/05-03 에서 nickname/message/tier 모두 payload 에 포함).
    void opts.state.transitionTo({
      kind: 'donation-incoming',
      tier: p.tier,
      event: {
        id: p.donationId,
        nickname: p.nickname,
        amount: p.amountKrw,
        message: p.message ?? '',
        receivedAt: Date.now(),
      },
    });
  }

  function onTtsReady(p: WidgetTtsReady): void {
    // T-05-27: 외부 URL 재생 거부 — apiBase 가 설정되었으면 prefix 검증.
    if (opts.apiBase && !p.audioUrl.startsWith(opts.apiBase)) {
      console.warn(
        '[widget-events] tts_ready rejected — audioUrl prefix does not match apiBase',
        { audioUrl: p.audioUrl, apiBase: opts.apiBase },
      );
      return;
    }
    void opts.state.transitionTo({
      kind: 'tts-playing',
      text: p.text,
      event: {
        id: p.donationId,
        // TTS payload 에는 nickname/amount 가 없으므로 placeholder — UI 는 text 만 표시
        nickname: '',
        amount: 0,
        message: p.text,
        receivedAt: Date.now(),
      },
    });
    opts.tts
      .play(p.audioUrl)
      .then(() => opts.state.transitionTo({ kind: 'idle' }))
      .catch((err: unknown) => {
        console.warn('[widget-events] tts playback failed', err);
        return opts.state.transitionTo({ kind: 'idle' });
      });
  }

  function onTtsFailed(p: WidgetTtsFailed): void {
    // transition 강제 없음. donation-incoming 은 자연 종료됨.
    console.info('[widget-events] tts_failed', p.reason);
  }

  function onFilterTriggered(p: WidgetFilterTriggered): void {
    // 시청자 모드에서는 render.ts shouldRenderForViewer 가 이미 숨김 처리 (DESIGN §3.1 #9).
    // 여기서는 preview 모드 용 상태 전이만 발행.
    void opts.state.transitionTo({
      kind: 'filter-triggered',
      blockedReason: p.reason,
      originalText: p.normalizedTextPreview,
    });
  }

  function onGoalReached(p: WidgetGoalReached): void {
    opts.state.pauseQueue();
    void opts.state.transitionTo({
      kind: 'goal-reached',
      goalLabel: p.title,
      goalAmount: p.targetKrw,
    });
    setTimeout(() => {
      opts.state.resumeQueue();
      void opts.state.transitionTo({ kind: 'idle' });
    }, goalDur);
  }

  function onState(_: unknown): void {
    // connect / reconnecting / disconnected-fatal 상태는 connectWidget 이 직접 발행하지만
    // 외부에서 주입받는 용도. 여기서는 default 로 no-op (main.ts 가 별도 state cb 로 연결).
  }

  return {
    onState: (): void => onState(undefined),
    onDonation,
    onTtsReady,
    onTtsFailed,
    onFilterTriggered,
    onGoalReached,
  };
}
